// stores/projectGenerationStore.js
import { defineStore } from 'pinia';
let _beforeUnloadHandler = null;
let _allowNextNavigation = false;

export const useProjectGenerationStore = defineStore('projectGeneration', {
  state: () => ({
    isGenerating: false,
    isMinimized: false,
    currentLoadingStep: 0,
    totalSteps: 0,
    loadingMessage: '',
    completedProjectId: null,
    generationFailed: false,
    _routerGuardRemover: null,
    _router: null,
    _modalStore: null,
  }),

  getters: {
    progress: (state) => {
      if (!state.totalSteps) return 0;
      return Math.round(
        ((state.currentLoadingStep - 1) / state.totalSteps) * 100
      );
    },
    isActive: (state) =>
      state.isGenerating || state.completedProjectId !== null,
  },

  actions: {
    // Call once from App.vue or a layout component on mount
    init(router, modalStore) {
      this._router = router;
      this._modalStore = modalStore;
    },
    _attachGuards() {
      if (typeof window === 'undefined') return;

      // Clear any stale listener first
      this._detachBeforeUnload();

      _beforeUnloadHandler = (e) => {
        if (!this.isGenerating) return;
        e.preventDefault();
        e.returnValue =
          'AI project generation is in progress. Leaving now will cancel it.';
        return e.returnValue;
      };
      window.addEventListener('beforeunload', _beforeUnloadHandler);

      if (this._router) {
        const removeGuard = this._router.beforeEach((_to, _from, next) => {
          if (_allowNextNavigation) {
            _allowNextNavigation = false;
            next();
            return;
          }
          if (!this.isGenerating || this.isMinimized) {
            next();
            return;
          }
          this._modalStore?.openConfirmationModal({
            title: 'Generation in Progress',
            message:
              'Your project is still being generated. Leaving now will cancel it.',
            confirmButtonLabel: '',
            declineButtonLabel: 'Leave & cancel',
            cancelButtonLabel: 'Stay here',
            showCloseButton: false,
            onDecline: () => {
              this.dismiss();
              next();
            },
            onCancel: () => next(false),
          });
        });
        this._routerGuardRemover = removeGuard;
      }
    },
    _detachBeforeUnload() {
      if (typeof window === 'undefined') return;
      if (_beforeUnloadHandler) {
        window.removeEventListener('beforeunload', _beforeUnloadHandler);
        _beforeUnloadHandler = null;
      }
    },

    _detachGuards() {
      this._detachBeforeUnload();
      if (this._routerGuardRemover) {
        this._routerGuardRemover();
        this._routerGuardRemover = null;
      }
    },
    allowNextNavigation() {
      _allowNextNavigation = true; // module-level, not this.*
    },

    startGeneration(totalSteps) {
      this.isGenerating = true;
      this.isMinimized = false;
      this.currentLoadingStep = 1;
      this.totalSteps = totalSteps;
      this.loadingMessage = 'Starting project creation...';
      this.completedProjectId = null;
      this.generationFailed = false;
      this._attachGuards();
    },

    updateProgress(step, message) {
      this.currentLoadingStep = step;
      this.loadingMessage = message;
    },

    completeGeneration(projectId) {
      this.isGenerating = false;
      this.completedProjectId = projectId;
      this.currentLoadingStep = this.totalSteps + 1;
      this.loadingMessage = 'Project created successfully!';
      this._detachGuards(); // remove beforeunload, keep pill visible
    },

    failGeneration() {
      this.isGenerating = false;
      this.generationFailed = true;
      this.loadingMessage = 'Generation failed.';
      this._detachGuards();
    },

    minimize() {
      this.isMinimized = true;
    },

    expand() {
      this.isMinimized = false;
    },

    dismiss() {
      this.isGenerating = false;
      this.isMinimized = false;
      this.currentLoadingStep = 0;
      this.totalSteps = 0;
      this.loadingMessage = '';
      this.completedProjectId = null;
      this.generationFailed = false;
      this._detachGuards();
    },

    setRouterGuardRemover(fn) {
      this._routerGuardRemover = fn;
    },

    _removeRouterGuard() {
      if (this._routerGuardRemover) {
        this._routerGuardRemover();
        this._routerGuardRemover = null;
      }
    },
  },
});
